// src/features/records/components/CalendarView/ListView/index.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { formatDateHeader, isSameDay, toDate } from '../utils/dateHelpers';
import { AvatarSkeleton } from '../../../../../components/avatars';
import RecordListItem from './RecordListItem';
import styles from './ListView.module.css';

const ListView = ({ 
  selectedDate, 
  records = [], 
  onClose, 
  onRecordClick, 
  loading = false,
  userProfiles = {},
  profilesLoading = false
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const selectedDateLabel = formatDateHeader(selectedDate, { locale, t });
  const [hoveredRecord, setHoveredRecord] = useState(null);
  const modalRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchOffset, setTouchOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // Filter records for the selected date only
  const dayRecords = useMemo(() => {
    if (!selectedDate) return [];
    
    const filteredRecords = records.filter(record => {
      if (!record.attributes?.date?.start) return false;
      
      const recordDate = toDate(record.attributes.date.start);
      if (!recordDate || isNaN(recordDate.getTime())) return false;
      
      return isSameDay(recordDate, selectedDate);
    });
    
    // Sort records by time
    return filteredRecords.sort((a, b) => {
      const aStart = toDate(a.attributes.date.start);
      const bStart = toDate(b.attributes.date.start);
      
      // All-day events first
      if (a.attributes.date.isAllDay && !b.attributes.date.isAllDay) return -1;
      if (!a.attributes.date.isAllDay && b.attributes.date.isAllDay) return 1;
      
      // Then by start time
      return aStart - bStart;
    });
  }, [records, selectedDate]);
  
  // Handle escape key and body scroll lock
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    // Lock body scroll for all devices
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Restore body scroll
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);
  
  // Reset touch offset when modal closes
  useEffect(() => {
    if (!isDragging) {
      setTouchOffset(0);
    }
  }, [isDragging]);
  
  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not its children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const handleBackdropTouchMove = (e) => {
    // Prevent scroll propagation when touching the backdrop
    if (e.target === e.currentTarget) {
      e.preventDefault();
    }
  };
  
  
  // Touch handlers for swipe-to-dismiss
  const handleTouchStart = (e) => {
    // Only handle touches on the grabber or header area
    const touch = e.touches[0];
    const element = e.target;
    const isGrabber = element.classList.contains(styles.grabber);
    const isHeader = element.closest(`.${styles.header}`);
    
    if (isGrabber || isHeader) {
      setTouchStart(touch.clientY);
      setIsDragging(true);
    }
  };
  
  const handleTouchMove = (e) => {
    if (!isDragging || touchStart === null) return;
    
    const touch = e.touches[0];
    const deltaY = touch.clientY - touchStart;
    
    // Only allow downward swipes
    if (deltaY > 0) {
      setTouchOffset(deltaY);
      e.preventDefault(); // Prevent scrolling while dragging
    }
  };
  
  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    const threshold = 100; // Minimum distance to trigger dismiss
    const velocity = touchOffset / 200; // Simple velocity calculation
    
    if (touchOffset > threshold || velocity > 0.5) {
      onClose();
    } else {
      // Animate back to original position
      setTouchOffset(0);
    }
    
    setIsDragging(false);
    setTouchStart(null);
  };
  
  // Loading skeleton component
  const RecordSkeleton = () => (
    <div className={styles.recordSkeleton}>
      <div className={styles.avatarSlot}>
        <AvatarSkeleton size="sm" className={styles.ownerAvatarSkeleton} />
      </div>
      <div className={styles.skeletonContent}>
        <div className={`${styles.skeleton} ${styles.skeletonTitle}`} />
        <div className={`${styles.skeleton} ${styles.skeletonBody}`} />
      </div>
      <div className={styles.skeletonTime}>
        <div className={`${styles.skeleton} ${styles.skeletonTimeText}`} />
      </div>
    </div>
  );
  
  return createPortal(
    <>
      {/* Modal backdrop */}
      <div 
        className={styles.modalBackdrop} 
        onClick={handleBackdropClick}
        onTouchMove={handleBackdropTouchMove}
        role="presentation"
      >
        <div 
          className={styles.listView}
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-label={t('records.views.calendar.list.dialog_aria', { date: selectedDateLabel })}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            transform: touchOffset > 0 ? `translateY(${touchOffset}px)` : 'translateY(0)',
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
        >
          {/* Grabber handle */}
          <div className={styles.grabber} aria-hidden="true" />
          
          {/* Header */}
          <div className={styles.header}>
            <h2 className={styles.headerTitle}>{selectedDateLabel}</h2>
            <button 
              className={styles.closeButton}
              onClick={onClose}
              aria-label={t('common.close')}
            >
              <X size={24} />
            </button>
          </div>
        
          {/* Content */}
          <div className={styles.container}>
            {loading ? (
              // Loading state
              <div className={styles.loadingContainer}>
                {[...Array(3)].map((_, i) => (
                  <RecordSkeleton key={i} />
                ))}
              </div>
            ) : dayRecords.length === 0 ? (
              // Empty state
              <div className={styles.emptyState}>
                <svg className={styles.emptyIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <h3 className={styles.emptyTitle}>{t('records.views.calendar.list.empty.title')}</h3>
                <p className={styles.emptyText}>{t('records.views.calendar.list.empty.description')}</p>
              </div>
            ) : (
              // Records list
              <div className={styles.recordsList}>
                {dayRecords.map(record => (
                  <RecordListItem
                    key={record.id}
                    record={record}
                    isHovered={hoveredRecord === record.id}
                    onMouseEnter={() => setHoveredRecord(record.id)}
                    onMouseLeave={() => setHoveredRecord(null)}
                    onClick={() => onRecordClick(record)}
                    userProfiles={userProfiles}
                    profilesLoading={profilesLoading}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ListView;
